import React, { useState, useEffect, useCallback } from 'react';
import { useData } from '../contexts/DataContext';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Droplets, Plus, Edit, Trash2, CheckCircle, AlertTriangle, Clock, RefreshCw,
  Route, Play, List, BarChart3, XCircle
} from 'lucide-react';

const FREQUENCIES = [
  { value: 'daily', label: 'Zilnic' },
  { value: 'weekly', label: 'Saptamanal' },
  { value: 'monthly', label: 'Lunar' },
  { value: 'hours_based', label: 'La ore functionare' },
];

const LubricationPage = () => {
  const { plants, selectedPlant } = useData();
  const [dashboard, setDashboard] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showPointDialog, setShowPointDialog] = useState(false);
  const [showRouteDialog, setShowRouteDialog] = useState(false);
  const [showExecuteDialog, setShowExecuteDialog] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [executions, setExecutions] = useState([]);

  const emptyPointForm = {
    equipment_id: '', point_name: '', lubricant_type: '', lubricant_brand: '',
    quantity: '', frequency: 'weekly', frequency_hours: '', notes: ''
  };
  const [pointForm, setPointForm] = useState(emptyPointForm);

  const emptyRouteForm = { name: '', plant_id: '', frequency: 'daily', description: '', point_ids: [] };
  const [routeForm, setRouteForm] = useState(emptyRouteForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const plantParam = selectedPlant && selectedPlant !== 'all' ? selectedPlant : undefined;
      const [dashRes, eqRes, execRes] = await Promise.all([
        api.get('/lubrication/dashboard', { params: { plant_id: plantParam } }),
        api.get('/equipment', { params: { plant_id: plantParam } }),
        api.get('/lubrication/executions', { params: { days: 30 } })
      ]);
      setDashboard(dashRes.data);
      setEquipment(eqRes.data);
      setExecutions(execRes.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  }, [selectedPlant]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreatePoint = async () => {
    try {
      const payload = { ...pointForm };
      if (payload.frequency_hours) payload.frequency_hours = parseInt(payload.frequency_hours);
      else delete payload.frequency_hours;
      await api.post('/lubrication/points', payload);
      toast.success('Punct de lubrifiere creat');
      setShowPointDialog(false);
      setPointForm(emptyPointForm);
      fetchData();
    } catch { toast.error('Eroare la creare'); }
  };

  const handleDeletePoint = async (id) => {
    if (!window.confirm('Stergeti punctul?')) return;
    try {
      await api.delete(`/lubrication/points/${id}`);
      toast.success('Punct sters');
      fetchData();
    } catch { toast.error('Eroare la stergere'); }
  };

  const handleCreateRoute = async () => {
    try {
      await api.post('/lubrication/routes', routeForm);
      toast.success('Ruta creata');
      setShowRouteDialog(false);
      setRouteForm(emptyRouteForm);
      fetchData();
    } catch { toast.error('Eroare la creare'); }
  };

  const handleDeleteRoute = async (id) => {
    if (!window.confirm('Stergeti ruta?')) return;
    try {
      await api.delete(`/lubrication/routes/${id}`);
      toast.success('Ruta stearsa');
      fetchData();
    } catch { toast.error('Eroare la stergere'); }
  };

  const handleExecutePoint = async (point) => {
    try {
      await api.post('/lubrication/execute', {
        point_id: point.id,
        equipment_id: point.equipment_id,
        lubricant_name: point.lubricant_type,
        quantity_used: point.quantity,
        observations: ''
      });
      toast.success(`Lubrifiere executata: ${point.point_name}`);
      fetchData();
    } catch { toast.error('Eroare la executie'); }
  };

  const handleExecuteRoute = async (route) => {
    if (!route.points || route.points.length === 0) {
      toast.error('Ruta nu are puncte');
      return;
    }
    try {
      const execs = route.points.map(p => ({
        point_id: p.id,
        equipment_id: p.equipment_id,
        lubricant_name: p.lubricant_type,
        quantity_used: p.quantity
      }));
      await api.post(`/lubrication/execute-route/${route.id}`, execs);
      toast.success(`Ruta executata: ${route.name} (${execs.length} puncte)`);
      fetchData();
    } catch { toast.error('Eroare la executie'); }
  };

  const pointStatus = (point) => {
    if (!point.last_executed) return 'overdue';
    const last = new Date(point.last_executed);
    const now = new Date();
    const freq = point.frequency;
    let interval = 7 * 24 * 60 * 60 * 1000;
    if (freq === 'daily') interval = 24 * 60 * 60 * 1000;
    if (freq === 'monthly') interval = 30 * 24 * 60 * 60 * 1000;
    const nextDue = new Date(last.getTime() + interval);
    if (nextDue < now) return 'overdue';
    if (nextDue < new Date(now.getTime() + 24 * 60 * 60 * 1000)) return 'due_today';
    return 'ok';
  };

  const statusBadge = (status) => {
    if (status === 'overdue') return <Badge className="bg-red-100 text-red-700">Intarziat</Badge>;
    if (status === 'due_today') return <Badge className="bg-orange-100 text-orange-700">Astazi</Badge>;
    return <Badge className="bg-green-100 text-green-700">OK</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  const d = dashboard || {};

  return (
    <div className="space-y-6" data-testid="lubrication-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">Gestionare Lubrifiere</h1>
          <p className="text-slate-500 mt-1">Planuri, rute si conformitate lubrifiere</p>
        </div>
      </div>

      {/* Dashboard KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{d.total_points || 0}</p>
            <p className="text-xs text-slate-500">Total Puncte</p>
          </CardContent>
        </Card>
        <Card className="border-green-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{d.ok || 0}</p>
            <p className="text-xs text-slate-500">La zi</p>
          </CardContent>
        </Card>
        <Card className="border-orange-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-orange-600">{d.due_today || 0}</p>
            <p className="text-xs text-slate-500">Astazi</p>
          </CardContent>
        </Card>
        <Card className="border-red-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-600">{d.overdue || 0}</p>
            <p className="text-xs text-slate-500">Intarziate</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0052CC]">{d.compliance_percent || 0}%</p>
            <p className="text-xs text-slate-500">Conformitate</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{d.routes_count || 0}</p>
            <p className="text-xs text-slate-500">Rute</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{d.recent_executions_7d || 0}</p>
            <p className="text-xs text-slate-500">Exec. 7 zile</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="dashboard" data-testid="lub-tab-points"><List className="w-4 h-4 mr-1" /> Puncte Lubrifiere</TabsTrigger>
          <TabsTrigger value="routes" data-testid="lub-tab-routes"><Route className="w-4 h-4 mr-1" /> Rute</TabsTrigger>
          <TabsTrigger value="history" data-testid="lub-tab-history"><Clock className="w-4 h-4 mr-1" /> Istoric</TabsTrigger>
        </TabsList>

        {/* Points Tab */}
        <TabsContent value="dashboard">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Puncte de Lubrifiere</CardTitle>
                <Button size="sm" onClick={() => { setPointForm(emptyPointForm); setShowPointDialog(true); }} data-testid="add-point-btn">
                  <Plus className="w-4 h-4 mr-1" /> Adauga Punct
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(!d.points || d.points.length === 0) ? (
                <p className="text-slate-400 text-center py-8">Niciun punct de lubrifiere definit. Adaugati primul punct.</p>
              ) : (
                <div className="space-y-2">
                  {d.points.map(point => {
                    const st = pointStatus(point);
                    return (
                      <div key={point.id} className="flex items-center gap-3 p-3 rounded-md border border-slate-100 hover:bg-slate-50">
                        <Droplets className={`w-5 h-5 flex-shrink-0 ${st === 'ok' ? 'text-green-500' : st === 'due_today' ? 'text-orange-500' : 'text-red-500'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900">{point.point_name}</p>
                          <p className="text-xs text-slate-400">
                            {point.equipment_name} | {point.lubricant_type} {point.lubricant_brand && `(${point.lubricant_brand})`} | {point.quantity} | {FREQUENCIES.find(f => f.value === point.frequency)?.label}
                          </p>
                          {point.last_executed && (
                            <p className="text-xs text-slate-400">Ultima exec: {new Date(point.last_executed).toLocaleDateString('ro-RO')} - {point.last_executed_by}</p>
                          )}
                        </div>
                        {statusBadge(st)}
                        <Button size="sm" variant="outline" onClick={() => handleExecutePoint(point)} data-testid={`exec-point-${point.id}`}>
                          <Play className="w-3 h-3 mr-1" /> Executa
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeletePoint(point.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Routes Tab */}
        <TabsContent value="routes">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Rute de Lubrifiere</CardTitle>
                <Button size="sm" onClick={() => { setRouteForm(emptyRouteForm); setShowRouteDialog(true); }} data-testid="add-route-btn">
                  <Plus className="w-4 h-4 mr-1" /> Creeaza Ruta
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(!d.routes || d.routes.length === 0) ? (
                <p className="text-slate-400 text-center py-8">Nicio ruta definita.</p>
              ) : (
                <div className="space-y-3">
                  {d.routes.map(route => (
                    <div key={route.id} className="p-4 rounded-md border border-slate-200 hover:bg-slate-50">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="text-sm font-semibold text-slate-900">{route.name}</p>
                          <p className="text-xs text-slate-400">
                            {route.plant_name} | {FREQUENCIES.find(f => f.value === route.frequency)?.label} | {route.points_count || 0} puncte
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleExecuteRoute(route)} data-testid={`exec-route-${route.id}`}>
                            <Play className="w-3 h-3 mr-1" /> Executa Ruta
                          </Button>
                          <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeleteRoute(route.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      {route.description && <p className="text-xs text-slate-500 mb-2">{route.description}</p>}
                      {route.points && route.points.length > 0 && (
                        <div className="space-y-1 mt-2 pl-4 border-l-2 border-slate-200">
                          {route.points.map(p => (
                            <div key={p.id} className="text-xs text-slate-600 flex items-center gap-2">
                              <Droplets className="w-3 h-3 text-blue-400" />
                              {p.equipment_name} - {p.point_name} ({p.lubricant_type})
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader><CardTitle className="text-lg">Istoric Executii (ultimele 30 zile)</CardTitle></CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <p className="text-slate-400 text-center py-8">Nicio executie inregistrata.</p>
              ) : (
                <div className="space-y-1">
                  {executions.map(ex => (
                    <div key={ex.id} className="flex items-center gap-3 py-2 px-3 rounded hover:bg-slate-50 border-b border-slate-50">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-slate-900">{ex.lubricant_name} {ex.quantity_used && `- ${ex.quantity_used}`}</p>
                        <p className="text-xs text-slate-400">{ex.executed_by_name} | {new Date(ex.executed_at).toLocaleString('ro-RO')}</p>
                      </div>
                      {ex.observations && <span className="text-xs text-slate-400">{ex.observations}</span>}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Point Dialog */}
      <Dialog open={showPointDialog} onOpenChange={setShowPointDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Adauga Punct de Lubrifiere</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Echipament</Label>
              <Select value={pointForm.equipment_id} onValueChange={v => setPointForm(p => ({ ...p, equipment_id: v }))} data-testid="point-equipment-select">
                <SelectTrigger><SelectValue placeholder="Selecteaza echipament" /></SelectTrigger>
                <SelectContent>
                  {equipment.map(eq => (
                    <SelectItem key={eq.id} value={eq.id}>{eq.name} ({eq.code})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Denumire punct</Label>
              <Input value={pointForm.point_name} onChange={e => setPointForm(p => ({ ...p, point_name: e.target.value }))} placeholder="ex: Rulment principal" data-testid="point-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tip lubrifiant</Label>
                <Input value={pointForm.lubricant_type} onChange={e => setPointForm(p => ({ ...p, lubricant_type: e.target.value }))} placeholder="ex: Ulei hidraulic" data-testid="point-lubricant-input" />
              </div>
              <div>
                <Label>Marca (optional)</Label>
                <Input value={pointForm.lubricant_brand} onChange={e => setPointForm(p => ({ ...p, lubricant_brand: e.target.value }))} placeholder="ex: Shell Tellus" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cantitate</Label>
                <Input value={pointForm.quantity} onChange={e => setPointForm(p => ({ ...p, quantity: e.target.value }))} placeholder="ex: 200ml" data-testid="point-qty-input" />
              </div>
              <div>
                <Label>Frecventa</Label>
                <Select value={pointForm.frequency} onValueChange={v => setPointForm(p => ({ ...p, frequency: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Observatii (optional)</Label>
              <Input value={pointForm.notes} onChange={e => setPointForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPointDialog(false)}>Anuleaza</Button>
            <Button onClick={handleCreatePoint} disabled={!pointForm.equipment_id || !pointForm.point_name || !pointForm.lubricant_type} data-testid="save-point-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Route Dialog */}
      <Dialog open={showRouteDialog} onOpenChange={setShowRouteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Creeaza Ruta de Lubrifiere</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Denumire ruta</Label>
              <Input value={routeForm.name} onChange={e => setRouteForm(p => ({ ...p, name: e.target.value }))} placeholder="ex: Ruta Hala 1 - Zilnic" data-testid="route-name-input" />
            </div>
            <div>
              <Label>Fabrica</Label>
              <Select value={routeForm.plant_id} onValueChange={v => setRouteForm(p => ({ ...p, plant_id: v }))}>
                <SelectTrigger><SelectValue placeholder="Selecteaza fabrica" /></SelectTrigger>
                <SelectContent>
                  {plants.map(pl => <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Frecventa</Label>
              <Select value={routeForm.frequency} onValueChange={v => setRouteForm(p => ({ ...p, frequency: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FREQUENCIES.filter(f => f.value !== 'hours_based').map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Puncte incluse</Label>
              <div className="max-h-48 overflow-y-auto border rounded-md p-2 space-y-1">
                {(d.points || []).map(p => (
                  <label key={p.id} className="flex items-center gap-2 py-1 cursor-pointer hover:bg-slate-50 px-2 rounded text-sm">
                    <input
                      type="checkbox"
                      checked={routeForm.point_ids.includes(p.id)}
                      onChange={e => {
                        if (e.target.checked) setRouteForm(prev => ({ ...prev, point_ids: [...prev.point_ids, p.id] }));
                        else setRouteForm(prev => ({ ...prev, point_ids: prev.point_ids.filter(id => id !== p.id) }));
                      }}
                    />
                    {p.equipment_name} - {p.point_name}
                  </label>
                ))}
                {(!d.points || d.points.length === 0) && <p className="text-xs text-slate-400">Creati mai intai puncte de lubrifiere</p>}
              </div>
            </div>
            <div>
              <Label>Descriere (optional)</Label>
              <Input value={routeForm.description} onChange={e => setRouteForm(p => ({ ...p, description: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRouteDialog(false)}>Anuleaza</Button>
            <Button onClick={handleCreateRoute} disabled={!routeForm.name || !routeForm.plant_id} data-testid="save-route-btn">Salveaza</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LubricationPage;
